import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { http } from '../lib/api';
import type { PlanDto } from '@livestreamx/shared-types';
import { useBrandingStore } from '../store/branding';
import { useUiStore } from '../store/ui';
import { useThemeSync } from '../components/layout';

const HERO_IMG =
  'https://images.pexels.com/photos/4050315/pexels-photo-4050315.jpeg?auto=compress&cs=tinysrgb&w=1100&h=760&fit=crop';
const ABOUT_IMG =
  'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=900&h=700&fit=crop';
const STREAM_IMG =
  'https://images.pexels.com/photos/7130540/pexels-photo-7130540.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop';

function Icon({ name }: { name: string }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true as const };
  switch (name) {
    case 'broadcast':
      return <svg {...common}><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" /><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" /><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19.1" /></svg>;
    case 'cloud':
      return <svg {...common}><path d="M17.5 19H9a7 7 0 1 1 6.7-9h1.8a4.5 4.5 0 1 1 0 9Z" /></svg>;
    case 'play':
      return <svg {...common}><polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" /></svg>;
    case 'record':
      return <svg {...common}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" /></svg>;
    case 'users':
      return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></svg>;
    case 'mail':
      return <svg {...common}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>;
    case 'phone':
      return <svg {...common}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" /></svg>;
    case 'map':
      return <svg {...common}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" /><circle cx="12" cy="10" r="3" /></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>;
    default:
      return <svg {...common}><circle cx="12" cy="12" r="10" /></svg>;
  }
}

const SERVICES = [
  { icon: 'broadcast', title: 'RTMP Live Stream Ingestion', body: 'Publish live video from any encoder via RTMP with a unique stream key and secure ingest URL.' },
  { icon: 'cloud', title: 'Live Stream Republishing', body: 'Restream to supported destinations such as YouTube, Facebook, Twitch and custom RTMP endpoints.' },
  { icon: 'play', title: 'M3U8 / HLS Playback', body: 'Low-latency HLS playback with adaptive bitrate so viewers enjoy smooth viewing on any device.' },
  { icon: 'record', title: 'Recording & Storage', body: 'Capture and store recordings where included in your plan, ready for on-demand playback.' },
  { icon: 'users', title: 'Viewer Capacity & Bandwidth', body: 'Streaming bandwidth and viewer limits scale according to the subscription plan you choose.' },
  { icon: 'shield', title: 'Subscription Management', body: 'Manage plans, renewals and account access from a single, secure dashboard with technical support.' },
];

// Mock plans data for landing page
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for individual creators and small streams',
    price: 499,
    period: '/month',
    features: [
      '1 concurrent stream',
      '3 destinations per stream',
      '5 total destinations',
      '10 GB storage',
      '100 GB bandwidth/month',
      'Up to 500 concurrent viewers',
      '720p max resolution',
      'Email support',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For growing creators and businesses',
    price: 1499,
    period: '/month',
    features: [
      '3 concurrent streams',
      '5 destinations per stream',
      '15 total destinations',
      '50 GB storage',
      '500 GB bandwidth/month',
      'Up to 2,000 concurrent viewers',
      '1080p max resolution',
      'Priority email & chat support',
      'Recording included',
    ],
    cta: 'Get Started',
    popular: true,
  },
  {
    id: 'business',
    name: 'Business',
    description: 'For professional broadcasters and teams',
    price: 3999,
    period: '/month',
    features: [
      '10 concurrent streams',
      '10 destinations per stream',
      '50 total destinations',
      '200 GB storage',
      '2 TB bandwidth/month',
      'Up to 10,000 concurrent viewers',
      '4K max resolution',
      '24/7 priority support',
      'Recording & clipping included',
      'Custom RTMP endpoints',
      'Analytics dashboard',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for large-scale operations',
    price: 9999,
    period: '/month',
    features: [
      'Unlimited concurrent streams',
      'Unlimited destinations',
      'Unlimited storage',
      'Unlimited bandwidth',
      'Unlimited viewers',
      '8K max resolution',
      'Dedicated account manager',
      'SLA guarantee',
      'Custom integrations',
      'White-label options',
      'On-premise deployment option',
      'SSO & advanced security',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

interface UiPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  period: string;
  features: string[];
  cta: string;
  popular: boolean;
}

/** Maps a plan row from the API into the shape the pricing cards render. */
function planToUi(plan: PlanDto): UiPlan {
  const features: string[] = [];
  if (plan.maxStreams) {
    features.push(`${plan.maxStreams} concurrent stream${plan.maxStreams > 1 ? 's' : ''}`);
  }
  if (plan.maxDestinationsPerStream > 0) {
    features.push(
      `${plan.maxDestinationsPerStream} destination${plan.maxDestinationsPerStream > 1 ? 's' : ''} per stream`,
    );
  }
  if (plan.maxTotalDestinations > 0) {
    features.push(
      `${plan.maxTotalDestinations} total destination${plan.maxTotalDestinations > 1 ? 's' : ''}`,
    );
  }
  if (plan.maxStorageGb > 0) {
    features.push(`${plan.maxStorageGb} GB storage`);
  }
  if (plan.maxBandwidthGb > 0) {
    features.push(`${plan.maxBandwidthGb} GB bandwidth/month`);
  }
  if (plan.viewLimit) {
    features.push(`Up to ${plan.viewLimit} concurrent viewer${plan.viewLimit > 1 ? 's' : ''}`);
  } else {
    features.push('Unlimited viewers');
  }
  if (plan.recordingEnabled) {
    features.push('Recording included');
  }
  if (plan.maxInboundBitrateMbps > 0) {
    features.push(`${plan.maxInboundBitrateMbps} Mbps ingest`);
  }
  const priceNum = plan.priceMonth !== null && Number(plan.priceMonth) > 0
      ? Number(plan.priceMonth)
      : plan.priceDay !== null
      ? Number(plan.priceDay)
      : 0;

  const period = plan.billingCycle === 'DAILY' ? '/day' : '/month';

  return {
    id: plan.id,
    name: plan.name,
    description: plan.description ?? '',
    price: priceNum,
    period,
    features,
    cta: 'Get Started',
    popular: false,
  };
}


export function LandingPage() {
  useThemeSync();
  const branding = useBrandingStore();
  const ui = useUiStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const name = branding.platformName?.trim() || 'RTMPHUB';
  const year = new Date().getFullYear();

  const navTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [plans, setPlans] = useState<UiPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadPlans = async () => {
      try {
        const data = await http.get<PlanDto[]>('/api/plans');
        if (!cancelled) {
          setPlans((data || []).map(planToUi));
        }
      } catch (err) {
        if (!cancelled) {
          setPlans([]);
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="landing-root" style={{ minHeight: '100vh', background: 'var(--background)', color: 'var(--text)' }}>
      <header className="landing-nav">
        <div className="landing-container landing-nav-inner">
          <a className="landing-brand" href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="landing-logo" /> : <span className="landing-logo-mark"><Icon name="broadcast" /></span>}
            <span className="landing-brand-name">{name}</span>
          </a>
          <nav className="landing-nav-links hide-mobile" aria-label="Primary">
            <button className="landing-nav-link" onClick={() => navTo('about')}>ABOUT US</button>
            <button className="landing-nav-link" onClick={() => navTo('plans')}>PLANS</button>
            <button className="landing-nav-link" onClick={() => navTo('services')}>Our Services</button>
            <button className="landing-nav-link" onClick={() => navTo('contact')}>CONTACT US</button>
          </nav>
          <div className="landing-nav-actions">
            <button className="icon-button" aria-label="Toggle theme" onClick={() => ui.toggleTheme()} title="Toggle theme">
              {ui.theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <Link to="/signup" className="btn secondary sm landing-signup-btn">Sign up</Link>
            <Link to="/login" className="btn primary sm landing-login-btn">Login</Link>
            <button className="landing-burger" aria-label="Toggle menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen((v) => !v)}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="landing-mobile-menu">
            <button className="landing-mobile-link" onClick={() => navTo('about')}>ABOUT US</button>
            <button className="landing-mobile-link" onClick={() => navTo('plans')}>PLANS</button>
            <button className="landing-mobile-link" onClick={() => navTo('services')}>Our Services</button>
            <button className="landing-mobile-link" onClick={() => navTo('contact')}>CONTACT US</button>
            <Link to="/login" className="btn primary sm" onClick={() => setMobileOpen(false)}>Login</Link>
          </div>
        )}
      </header>

      <main id="top">
        <section className="landing-hero">
          <div className="landing-blob landing-blob-1" aria-hidden />
          <div className="landing-blob landing-blob-2" aria-hidden />
          <div className="landing-container landing-hero-inner">
            <div className="landing-hero-copy">
              <span className="landing-eyebrow">Live streaming, simplified</span>
              <h1 className="landing-hero-title">Stream once. Broadcast everywhere.</h1>
              <p className="landing-hero-sub">
                {name} is an online streaming technology platform by Jak techdrivesolution. We provide subscription-based streaming
                infrastructure to publish, manage and scale live video over the internet — RTMP ingestion, republishing, HLS playback,
                recording and more.
              </p>
              <div className="landing-hero-cta">
                <Link to="/login" className="btn primary">Login to your account</Link>
                <Link to="/signup" className="btn secondary">Sign up</Link>
              </div>
              <div className="landing-hero-badges">
                <span className="landing-badge"><Icon name="shield" /> Secure & reliable</span>
                <span className="landing-badge"><Icon name="cloud" /> Cloud-ready</span>
                <span className="landing-badge"><Icon name="users" /> Scalable plans</span>
              </div>
            </div>
            <div className="landing-hero-media">
              <div className="landing-hero-img-wrap">
                <img src={HERO_IMG} alt="Live streaming studio setup" className="landing-hero-img" loading="eager" />
                <div className="landing-hero-float landing-hero-float-1">
                  <Icon name="broadcast" /> Live in 4 destinations
                </div>
                <div className="landing-hero-float landing-hero-float-2">
                  <Icon name="play" /> HLS playback ready
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="landing-section">
          <div className="landing-container landing-about">
            <div className="landing-about-media">
              <img src={ABOUT_IMG} alt="Team collaborating on streaming technology" className="landing-about-img" loading="lazy" />
              <div className="landing-about-deco" aria-hidden />
            </div>
            <div className="landing-about-copy">
              <span className="landing-eyebrow">About us</span>
              <h2 className="landing-section-title">About {name}</h2>
              <p>
                {name} is an online streaming technology platform operated by <strong>Jak techdrivesolution</strong>. We provide
                subscription-based streaming infrastructure for customers who need to publish and manage live video streams over the
                internet.
              </p>
              <p className="text-muted">
                Our platform is built for creators, businesses and broadcasters who want reliable, scalable live streaming without
                the complexity of managing their own infrastructure.
              </p>
              <ul className="landing-list">
                <li><Icon name="broadcast" /> RTMP live stream ingestion.</li>
                <li><Icon name="cloud" /> Live stream republishing to supported destinations.</li>
                <li><Icon name="play" /> M3U8 / HLS playback support.</li>
                <li><Icon name="users" /> Streaming bandwidth and viewer capacity per plan.</li>
                <li><Icon name="record" /> Recording and storage features where included.</li>
                <li><Icon name="shield" /> Subscription management and technical support.</li>
              </ul>
              <div className="landing-about-actions">
                <Link to="/login" className="btn primary sm">Login</Link>
                <button className="btn ghost sm" onClick={() => navTo('contact')}>Talk to us</button>
              </div>
            </div>
          </div>
        </section>

        <section id="plans" className="landing-section landing-section-alt">
          <div className="landing-container">
            <div className="landing-section-head">
              <span className="landing-eyebrow">Pricing</span>
              <h2 className="landing-section-title">Simple, Transparent Pricing</h2>
              <p className="text-muted landing-section-sub">
                Choose the plan that fits your streaming needs. All plans include RTMP ingestion, multi-destination republishing,
                HLS playback, and subscription management. Upgrade or downgrade anytime.
              </p>
            </div>
            <div className="landing-plans-scroll" role="region" aria-label="Pricing plans">
              {plansLoading && plans.length === 0 ? (
                <p className="text-muted" style={{ padding: '2rem 0', textAlign: 'center' }}>Loading plans…</p>
              ) : (
                <div className="landing-plans-track">
                  {plans.map((plan) => (
                  <article key={plan.id} className={`landing-plan-card ${plan.popular ? 'popular' : ''}`}>
                    {plan.popular && <span className="landing-plan-badge">Most Popular</span>}
                    <div className="landing-plan-head">
                      <h3 className="landing-plan-name">{plan.name}</h3>
                      <p className="landing-plan-desc">{plan.description}</p>
                    </div>
                    <div className="landing-plan-price">
                      <span className="landing-plan-amount">₹{plan.price.toLocaleString('en-IN')}</span>
                      <span className="landing-plan-period">{plan.period}</span>
                    </div>
                    <ul className="landing-plan-features">
                      {plan.features.map((feature, idx) => (
                        <li key={idx}>
                          <span className="landing-plan-feature-icon"><Icon name="shield" /></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="landing-plan-cta">
                      {plan.popular ? (
                        <Link to="/signup" className="btn primary w-full">{plan.cta}</Link>
                      ) : (
                        <Link to="/signup" className="btn secondary w-full">{plan.cta}</Link>
                      )}
                    </div>
                  </article>
                  ))}
                </div>
              )}
            </div>
            <div className="landing-plans-hint" aria-hidden="true">
              <Icon name="play" /> Scroll horizontally to view all plans
            </div>
          </div>
        </section>

        <section id="services" className="landing-section landing-section-alt">
          <div className="landing-container">
            <div className="landing-section-head">
              <span className="landing-eyebrow">Our services</span>
              <h2 className="landing-section-title">Our Services</h2>
              <p className="text-muted landing-section-sub">
                Customers can select from available subscription plans based on their streaming requirements. Each plan specifies its
                applicable duration, bandwidth limits, recording or storage allowances, viewer capacity and other included features.
              </p>
            </div>
            <div className="landing-services-grid">
              {SERVICES.map((s) => (
                <div key={s.title} className="landing-service-card">
                  <span className="landing-service-icon"><Icon name={s.icon} /></span>
                  <h3>{s.title}</h3>
                  <p className="text-muted">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="landing-service-banner">
              <img src={STREAM_IMG} alt="Live broadcast control room" className="landing-service-banner-img" loading="lazy" />
              <div className="landing-service-banner-copy">
                <h3>Service activation</h3>
                <p>
                  Access to the purchased services is provided after successful payment verification and account activation. The features
                  available to each customer depend on the selected plan.
                </p>
                <Link to="/login" className="btn primary sm">Login to manage your plan</Link>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="landing-section">
          <div className="landing-container">
            <div className="landing-section-head">
              <span className="landing-eyebrow">Contact us</span>
              <h2 className="landing-section-title">CONTACT US</h2>
              <p className="text-muted landing-section-sub">
                For assistance with streaming configuration, subscription plans, payments, account access, renewals or refunds, please
                contact our support team.
              </p>
            </div>
            <div className="landing-contact-grid">
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="map" /></span>
                <div>
                  <h4>Business name</h4>
                  <p>Jak techdrivesolution</p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="cloud" /></span>
                <div>
                  <h4>Website</h4>
                  <p><a href="https://rtmphub.in" target="_blank" rel="noopener noreferrer">https://rtmphub.in</a></p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="mail" /></span>
                <div>
                  <h4>Support email</h4>
                  <p>{branding.supportEmail?.trim() ? <a href={`mailto:${branding.supportEmail.trim()}`}>{branding.supportEmail.trim()}</a> : 'Enter active support email'}</p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="phone" /></span>
                <div>
                  <h4>Phone</h4>
                  <p>{branding.supportWhatsapp?.trim() ? <a href={`https://wa.me/${branding.supportWhatsapp.trim().replace(/\D/g, '')}`}>{branding.supportWhatsapp.trim()}</a> : 'Enter active business contact number'}</p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="map" /></span>
                <div>
                  <h4>Business address</h4>
                  <p>{branding.address?.trim() || 'Enter complete business address'}</p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="clock" /></span>
                <div>
                  <h4>Support hours</h4>
                  <p>{branding.supportHours?.trim() || 'Enter actual support days and hours'}</p>
                </div>
              </div>
              <div className="landing-contact-card">
                <span className="landing-contact-icon"><Icon name="phone" /></span>
                <div>
                  <h4>Another phone</h4>
                  <p>{branding.anotherPhone?.trim() ? <a href={`tel:${branding.anotherPhone.trim()}`}>{branding.anotherPhone.trim()}</a> : 'Enter alternate contact number'}</p>
                </div>
              </div>

            </div>
            {branding.footerText?.trim() ? <p className="text-muted text-sm landing-contact-note">{branding.footerText.trim()}</p> : null}
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <div className="landing-footer-col">
            <div className="landing-brand landing-footer-brand">
              {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="landing-logo" /> : <span className="landing-logo-mark"><Icon name="broadcast" /></span>}
              <span className="landing-brand-name">{name}</span>
            </div>
            <p className="text-muted text-sm">{branding.footerText?.trim() || 'Subscription-based live streaming infrastructure by Jak techdrivesolution.'}</p>
            {branding.supportEmail?.trim() ? <p className="text-sm"><a href={`mailto:${branding.supportEmail.trim()}`}>{branding.supportEmail.trim()}</a></p> : null}
          </div>
          <div className="landing-footer-col">
            <h4>Quick links</h4>
            <button className="landing-footer-link" onClick={() => navTo('about')}>About us</button>
            <button className="landing-footer-link" onClick={() => navTo('plans')}>Plans</button>
            <button className="landing-footer-link" onClick={() => navTo('services')}>Our services</button>
            <button className="landing-footer-link" onClick={() => navTo('contact')}>Contact us</button>
            <Link to="/login" className="landing-footer-link">Login</Link>
            <Link to="/signup" className="landing-footer-link">Sign up</Link>
          </div>
          <div className="landing-footer-col">
            <h4>Legal</h4>
            <Link to="/terms" className="landing-footer-link">TERMS &amp; CONDITIONS</Link>
            <Link to="/privacy" className="landing-footer-link">PRIVACY POLICY</Link>
            <Link to="/refund" className="landing-footer-link">REFUND &amp; CANCELLATION POLICY</Link>
          </div>
          <div className="landing-footer-col">
            <h4>Contact</h4>
            <p className="text-sm">Business: Jak techdrivesolution</p>
            <p className="text-sm">Website: <a href="https://rtmphub.in" target="_blank" rel="noopener noreferrer">https://rtmphub.in</a></p>
            <p className="text-sm">Address: {branding.address?.trim() || 'Enter complete business address'}</p>
            <p className="text-sm">Support hours: {branding.supportHours?.trim() || 'Enter actual support days and hours'}</p>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <div className="landing-container">
            <p className="text-muted text-xs">© {year} Jak techdrivesolution. All rights reserved. {name} — RTMPHUB.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
